import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { formatDateOnly } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { DataTable } from "@/components/app/patterns/data-table";
import { DataTableToolbar } from "@/components/app/patterns/data-table-toolbar";
import { PersonCell } from "@/components/app/patterns/person-cell";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { OptionSelect, SelectOption } from "@/components/ui/option-select";
import { useFacility } from "@/lib/facility/facility";
import { Printer } from "@/lib/icons";
import { orpc, type orpcClient, type Output } from "@/lib/orpc";
import { guardRoute } from "@/lib/policy/route-guard";
import { optionalOneOf, optionalString, KEEP_PLACE } from "@/lib/search";
import { cn } from "@/lib/utils";
import { CategoryBadge } from "@/routes/_authenticated/_care/expiring-auths/-components/category-badge";
import { expiryText, IssueBadge, REASON_LABELS } from "./-components/auth-cells";
import { SeverityChips } from "@/routes/_authenticated/_care/expiring-auths/-components/severity-chips";
import { linkClass } from "@/components/app/lists/record-link-shared";

const WINDOWS = ["7", "14", "30", "60", "90"] as const;
type AuthSearch = { days?: (typeof WINDOWS)[number]; severity?: string };

export const Route = createFileRoute("/_authenticated/_care/expiring-auths/")({
  validateSearch: (search: Record<string, unknown>): AuthSearch => ({
    days: optionalOneOf(WINDOWS, optionalString(search.days)),
    severity: optionalString(search.severity)?.slice(0, 40) || undefined,
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "expiring-auths"),
  component: ExpiringAuthorizations,
});

type Row = Output<typeof orpcClient.financials.reports.expiringAuthorizations>["items"][number];

const DASH = <span className="text-muted-foreground">-</span>;

/* No column is sorted until one is chosen: the server sends the list in the order it
   is worked (missing, then expired, then expiring soonest first). */
const COLUMNS: ColumnDef<Row>[] = [
  {
    id: "resident",
    header: "Resident",
    accessorFn: (row) => `${row.residentName} ${row.roomNumber ?? ""}`,
    sortingFn: (a, b) => a.original.residentName.localeCompare(b.original.residentName),
    enableHiding: false,
    cell: ({ row: { original: row } }) => (
      <PersonCell
        kind="resident"
        size="sm"
        name={row.residentName}
        meta={[row.roomNumber ? `Room ${row.roomNumber}` : null, row.facilityName]}
        renderLink={(label) => (
          <Link
            to="/residents/$residentId"
            params={{ residentId: row.residentId }}
            className={cn(linkClass, "flex")}
          >
            {label}
          </Link>
        )}
      />
    ),
  },
  {
    id: "category",
    header: "Category",
    accessorFn: (row) => (row.category === "medicaid_2065d" ? "Medicaid 2065D" : "MCO"),
    meta: { filter: "select", nowrap: true },
    cell: ({ row: { original: row } }) => <CategoryBadge category={row.category} />,
  },
  {
    id: "severity",
    header: "Issue",
    accessorKey: "severity",
    enableGlobalFilter: false,
    meta: { nowrap: true },
    cell: ({ row: { original: row } }) => <IssueBadge severity={row.severity} />,
  },
  {
    id: "reason",
    header: "Reason",
    accessorFn: (row) => REASON_LABELS[row.reason] ?? row.reason,
    meta: { className: "text-muted-foreground", filter: "select", priority: 2, clamp: "lg" },
  },
  {
    id: "authNumber",
    header: "Auth #",
    accessorFn: (row) => row.authorizationNumber ?? "",
    cell: ({ getValue }) => getValue<string>() || DASH,
    meta: { nowrap: true, priority: 2 },
  },
  {
    id: "authorizedBy",
    header: "Authorized by",
    accessorFn: (row) => row.authorizedBy ?? "",
    cell: ({ getValue }) => getValue<string>() || DASH,
    meta: { className: "text-muted-foreground", filter: "select", priority: 3, clamp: "md" },
  },
  {
    id: "endDate",
    header: "End date",
    accessorFn: (row) => (row.endDate ? String(row.endDate) : ""),
    enableGlobalFilter: false,
    meta: { nowrap: true },
    cell: ({ row: { original: row } }) => (row.endDate ? formatDateOnly(row.endDate) : DASH),
  },
  {
    id: "expiry",
    header: "Expiry",
    accessorFn: (row) => row.daysUntilExpiry ?? 9999,
    enableGlobalFilter: false,
    meta: { align: "right", nowrap: true },
    cell: ({ row: { original: row } }) =>
      row.severity === "missing" || row.daysUntilExpiry === null ? (
        DASH
      ) : (
        <span className={cn(row.severity === "expired" && "text-destructive font-medium")}>
          {expiryText(row)}
        </span>
      ),
  },
];

/** Authorizations about to lapse, while there is still time to renew them. */
function ExpiringAuthorizations() {
  const { facilityId } = useFacility();
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<AuthSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const days = params.days ?? "30";
  const setDays = (next: string) =>
    patch({ days: next === "30" ? undefined : optionalOneOf(WINDOWS, next) });
  const severity = params.severity ?? "all";
  const setSeverity = (next: string) => patch({ severity: next === "all" ? undefined : next });

  const report = useQuery(
    orpc.financials.reports.expiringAuthorizations.queryOptions({
      input: { facilityId, days: Number(days) },
    }),
  );
  const items = report.data?.items ?? [];
  const counts = report.data?.counts;
  const shown = items.filter((row) => severity === "all" || row.severity === severity);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Expiring authorizations"
        count={report.isSuccess ? items.length : undefined}
        meta={
          report.isPending
            ? "Loading…"
            : `${items.length} in the next ${days} days, ${counts?.expired ?? 0} already expired`
        }
        actions={
          <Button variant="outline" onClick={() => window.print()}>
            <Printer className="size-4" />
            Print
          </Button>
        }
      />

      {report.isError ? (
        <Card className="gap-0 py-0">
          <ReadFailed
            className="p-4"
            message="Could not load the expiring authorizations."
            error={report.error}
            onRetry={() => void report.refetch()}
            retrying={report.isFetching}
          />
        </Card>
      ) : (
        <DataTable
          data={shown}
          columns={COLUMNS}
          noun="authorizations"
          rowId={(row) => row.id}
          pending={report.isPending}
          renderToolbar={(table) => (
            <DataTableToolbar
              table={table}
              searchPlaceholder="Resident name or room…"
              chips={<SeverityChips counts={counts} value={severity} onChange={setSeverity} />}
              trailing={
                <OptionSelect
                  id="expiring-window"
                  aria-label="Window"
                  value={days}
                  onChange={(event) => setDays(event.target.value)}
                >
                  {WINDOWS.map((value) => (
                    <SelectOption key={value} value={value}>
                      Next {value} days
                    </SelectOption>
                  ))}
                </OptionSelect>
              }
            />
          )}
          empty={
            <EmptyState
              title={severity === "all" ? "Nothing lapsing" : "Nothing matches these filters"}
              description={
                severity === "all"
                  ? `No authorization expires in the next ${days} days.`
                  : "Widen the search or clear a filter to see more."
              }
              action={
                severity === "all" ? undefined : (
                  <Button variant="outline" onClick={() => setSeverity("all")}>
                    Clear filters
                  </Button>
                )
              }
            />
          }
        />
      )}
    </div>
  );
}
